import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";
import ConfirmationModal from "../../Common/ConfirmationModal";
import API from "../../../util/api";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";
import { useSelector, useDispatch } from "react-redux";
import { storeSubscriptionSchoolLimits } from "../../../sagas/actions";
import { actions } from "../../../util/actions";

const LINK_HOVER_COLOR = "#F7941D";

const breakpoints = {
  tablet: 768,
  phone:  480,
};

const View = styled.div`
  flex: 1 1 0;
  min-width: 0;
  padding: 1.5em 0em;
  background: #fff;

  @media (max-width: ${breakpoints.tablet}px) {
    padding: 1em 0;
  }

  @media (max-width: ${breakpoints.phone}px) {
    padding: 0.75em 0;
  }
`;

const Controls = styled.div`
  display: flex;
  padding: 0em 1.5em;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 1em;
  label {
    font-size: 14px;
    margin-right: 8px;
    color: #000;
    font-weight: bold;
  }
  select,
  input {
    padding: 5px 10px;
    font-size: 14px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 5px;
  }
  select { min-width: 120px; }
  input { width: 200px; }

  div {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  @media (max-width: ${breakpoints.tablet}px) {
    flex-direction: column;
    gap: 12px;
    padding: 0 0.75em;

    div {
      width: 100%;
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      align-items: center;
    }

    label {
      margin-right: 0;
    }

    input {
      width: 100%;
      max-width: none;
    }

    select {
      width: 100%;
    }
  }

  @media (max-width: ${breakpoints.phone}px) {
    padding: 0 0.5em;

    input {
      width: 100%;
      max-width: none;
    }

    select {
      width: 100%;
    }
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  width: 100%;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  ${adminTable};
  min-width: 720px;

  thead th {
    ${adminTableHeaderCell};
    position: relative;
    background: #fff;
    border-bottom: 1px solid #ccc;
    .sort-icons {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      flex-direction: column;
      font-size: 10px;
      span {
        cursor: pointer;
        line-height: 10px;
        color: #9CA3AF;
        display: block;
      }
      .active {
        color: #A855F7;
      }
    }
  }

  tbody td {
    ${adminTableDataCell};
    padding: 20px;
    vertical-align: top;
  }

  .LinkButton {
    margin-left: 0.5em;
    font-size: 0.85em;
    cursor: pointer;
    color: ${({ colors }) => colors.buttonBackground};
    border-bottom: 1px dotted ${({ colors }) => colors.buttonBackground};
    transition: color 0.2s ease, border-color 0.2s ease;
    &:hover {
      color: ${LINK_HOVER_COLOR};
      border-bottom-color: ${LINK_HOVER_COLOR};
      text-decoration: none;
    }
  }
    @media (max-width: ${breakpoints.phone}px) {
    font-size: 12px;
    thead th, tbody td {
      padding: 6px 8px;
    }
  }
`;

const InfoText = styled.div`
  margin: 10px 0;
  font-size: 12px;
  color: ${({ colors }) => colors.labelText};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5em;
  padding-right: 10px;
  button {
    padding: 5px 10px;
    border: 1px solid ${({ colors }) => colors.border};
    background: ${({ colors }) => colors.cardBackground};
    border-radius: 5px;
    cursor: pointer;
    &:disabled {
      background: ${({ colors }) => colors.disabledBackground};
      cursor: not-allowed;
    }
    &:hover:not(:disabled) {
      background: ${({ colors }) => colors.buttonBackground};
      color: white;
    }
  }
  @media (max-width: ${breakpoints.tablet}px) {
    padding-right: 0.5em;
  }
  @media (max-width: ${breakpoints.phone}px) {
    justify-content: center;
    flex-wrap: wrap;
    gap: 4px;
    padding-right: 0.25em;

    button {
      flex: 1 1 auto;
      padding: 6px;
      font-size: 12px;
    }
  }
`;

const StatusNotice = styled.div`
  font-size: 19.5px;
  margin-bottom: 1em;
  padding: 0.75em 1em;
  border-radius: 4px;
  background: transparent;
  color: red;
`;

const RemovalMessage = styled.div`
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ModalSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
`;

const AffectedList = styled.div`
  border: 1px solid ${({ colors }) => colors.border || "#d1d5db"};
  border-radius: 6px;
  background: #f9fafb;
  max-height: 200px;
  overflow-y: auto;
  padding: 8px;
`;

const AffectedRow = styled.div`
  padding: 6px 4px;
  border-bottom: 1px solid #e5e7eb;
  &:last-child {
    border-bottom: none;
  }
`;

const AffectedName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
`;

const AffectedMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const ListNotice = styled.div`
  padding: 10px 6px;
  text-align: center;
  font-size: 13px;
  color: #6b7280;
`;

const OptionsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #111827;
  line-height: 1.4;

  input {
    margin-top: 2px;
  }
`;

const OptionHint = styled.span`
  color: #4b5563;
`;

const getSubscriptionStatus = (startDate, endDate) => {
  if (!startDate || !endDate) return "unknown";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T23:59:59`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "unknown";
  }
  if (today < start) return "future";
  if (today > end) return "past";
  return "current";
};

const STATUS_MESSAGES = {
  past: "You are viewing Schools associated with a past invoice.",
  future: "You are viewing Schools associated with a future invoice.",
};

const getTeacherDisplayName = (teacher) => {
  if (!teacher) return "Unnamed teacher";
  const fullName = [teacher.first_name, teacher.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return (
    teacher.educator_name ||
    teacher.name ||
    fullName ||
    teacher.email ||
    "Unnamed teacher"
  );
};

const getTeacherMeta = (teacher) => {
  if (!teacher) return "";
  const email = teacher.email || "";
  const school = teacher.school_name || teacher.org_name || "";
  if (email && school) return `${email} • ${school}`;
  return email || school || "";
};

export default function SchoolDistrictsListing({ colors, onChange }) {
  const dispatch = useDispatch();
  const current = useSelector((s) => s.auth.selected_subscriptions) || {};
  const { invoice_id, customer_id } = current;
  const [schools, setSchools] = useState([]);
  const [total, setTotal] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [sortKey, setSortKey] = useState("org_name");
  const [sortOrder, setSortOrder] = useState("ASC");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toRemove, setToRemove] = useState(null);
  const [doubleConfirmOpen, setDoubleConfirmOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [removeMessage, setRemoveMessage] = useState(null);
  const [resultStatus, setResultStatus] = useState("Success");
  const [affectedTeachers, setAffectedTeachers] = useState([]);
  const [affectedTeachersTotal, setAffectedTeachersTotal] = useState(0);
  const [affectedTeachersLoading, setAffectedTeachersLoading] = useState(false);
  const [affectedTeachersError, setAffectedTeachersError] = useState("");
  const [deleteDownloads, setDeleteDownloads] = useState(false);
  const [deleteCertifications, setDeleteCertifications] = useState(false);
  const subscriptionStatus = useMemo(
    () =>
      getSubscriptionStatus(
        current.start_date || current.start,
        current.end_date || current.end
      ),
    [current.start_date, current.start, current.end_date, current.end]
  );
  const statusMessage = STATUS_MESSAGES[subscriptionStatus] || "";
  const stateCode = current?.org_state;

  const refreshSubscriptionSchools = useCallback(() => {
    if (!stateCode || !invoice_id) return Promise.resolve();
    return API.getSubscriptionSchools({ state: stateCode })
      .then((resp) => {
        const list = resp?.schools || [];
        dispatch({
          type: actions.STORE_SUBSCRIPTION_SCHOOLS,
          schoolsByState: { [stateCode]: list },
        });
      })
      .catch((err) => {
        console.error("Failed to refresh subscription schools:", err);
      });
  }, [stateCode, invoice_id, dispatch]);

  const clearSubscriptionSchoolsCache = useCallback(() => {
    if (!stateCode) return;
    dispatch({
      type: actions.STORE_SUBSCRIPTION_SCHOOLS,
      schoolsByState: { [stateCode]: [] },
    });
  }, [stateCode, dispatch]);

  // fetch paginated list
  const fetchPage = useCallback(() => {
    if (!invoice_id) return;
    API.getSchools({
      invoice_id,
      page: page - 1,
      items_per_page: itemsPerPage,
      order_by: sortKey,
      order: sortOrder,
      search: debouncedSearch,
    })
      .then((resp) => {
        const nextSchools = resp.schools || [];
        const parsedTotal = Number(resp.total);
        const responseTotal = Number.isFinite(parsedTotal)
          ? parsedTotal
          : nextSchools.length || 0;
        setSchools(nextSchools);
        setTotal(responseTotal);
        dispatch(
          storeSubscriptionSchoolLimits({
            allowed: resp.allowed_schools,
            total: responseTotal,
            invoiceId: invoice_id,
          })
        );
      })
      .catch((err) => {
        console.error("Failed to load schools:", err);
        setSchools([]);
        setTotal(0);
        dispatch(
          storeSubscriptionSchoolLimits({
            total: 0,
            invoiceId: invoice_id,
          })
        );
      });
  }, [invoice_id, page, itemsPerPage, sortKey, sortOrder, debouncedSearch, dispatch]);

  const loadAffectedTeachers = useCallback((school) => {
    const schoolId = school?.school_id;
    if (!invoice_id || !schoolId) {
      setAffectedTeachers([]);
      setAffectedTeachersTotal(0);
      return;
    }

    setAffectedTeachersLoading(true);
    setAffectedTeachersError("");
    API.getTeachersBySchool({
      invoice_id,
      customer_id,
      school_id: schoolId,
    })
      .then((resp) => {
        const teachers = resp?.teachers || [];
        const normalized = teachers.map((t) => ({
          ...t,
          id:
            t.user_id ||
            t.implementation_id ||
            t.id ||
            t.customer_user_id ||
            t.email,
        }));
        setAffectedTeachers(normalized);
        setAffectedTeachersTotal(
          resp?.total_teachers ?? resp?.total ?? normalized.length
        );
      })
      .catch((err) => {
        console.error("Failed to load affected teachers:", err);
        setAffectedTeachers([]);
        setAffectedTeachersTotal(0);
        setAffectedTeachersError(
          "Unable to load the affected teacher list."
        );
      })
      .finally(() => setAffectedTeachersLoading(false));
  }, [invoice_id, customer_id]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  // remove flow
  const onRemoveClick = (school) => {
    setToRemove(school);
    setConfirmOpen(true);
    setDoubleConfirmOpen(false);
    setDeleteDownloads(false);
    setDeleteCertifications(false);
    loadAffectedTeachers(school);
  };

  const resetRemovalState = () => {
    setConfirmOpen(false);
    setDoubleConfirmOpen(false);
    setToRemove(null);
    setDeleteDownloads(false);
    setDeleteCertifications(false);
    setAffectedTeachers([]);
    setAffectedTeachersTotal(0);
    setAffectedTeachersLoading(false);
    setAffectedTeachersError("");
  };

  const handleRemoveConfirm = () => {
    if (!toRemove?.school_id) return;
    setResultStatus("Success");
    setDoubleConfirmOpen(false);
    const hasTeachers = affectedTeachersError
      ? true
      : (Number.isFinite(affectedTeachersTotal)
          ? affectedTeachersTotal
          : affectedTeachers.length) > 0;
    const payload = {
      invoice_id,
      customer_id,
      school_id: toRemove.school_id,
    };
    if (hasTeachers) {
      payload.remove_users = 1;
      payload.delete_downloads = deleteDownloads ? 1 : 0;
      payload.delete_certifications = deleteCertifications ? 1 : 0;
    } else {
      payload.delete_downloads = 0;
    }
    API.removeSchool(payload)
      .then((resp) => {
        // refresh list
        const status = resp?.status === "Failed" ? "Failed" : "Success";
        setRemoveMessage(
          resp.message ||
          (status === "Failed" ? "Failed to remove school." : "School removed.")
        );
        setResultStatus(status);
        fetchPage();
        if (status !== "Failed") {
          clearSubscriptionSchoolsCache();
          refreshSubscriptionSchools();
        }
      })
      .catch(err => {
        console.error("Removal failed:", err);
        setRemoveMessage(
          err?.response?.data?.message ||
          err?.message ||
          "Failed to remove school."
        );
        setResultStatus("Failed");
      })
      .finally(() => {
        resetRemovalState();
        setResultOpen(true);
      });
  };

  const handleRemovalContinue = () => {
    setConfirmOpen(false);
    setDoubleConfirmOpen(true);
  };

  const handleDoubleConfirmCancel = () => {
    setDoubleConfirmOpen(false);
    setConfirmOpen(true);
  };

  const handleResultClose = () => {
    setResultOpen(false);
    setResultStatus("Success");
  };

  // sorting
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder((o) => (o === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortKey(key);
      setSortOrder("ASC");
    }
    setPage(1);
  };

  // client‐side filter
  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return schools.filter((sch) => {
      if (!term) return true;
      return (
        sch.org_name.toLowerCase().includes(term) ||
        sch.org_type_desc.toLowerCase().includes(term) ||
        sch.city.toLowerCase().includes(term)
      );
    });
  }, [schools, search]);

  const display = filtered;

  const pageCount = useMemo(() => {
    if (itemsPerPage < 0) return 1;
    if (!itemsPerPage) return 1;
    return Math.max(1, Math.ceil(total / itemsPerPage));
  }, [itemsPerPage, total]);

  const effectivePageSize = itemsPerPage < 0 ? total : itemsPerPage;
  const start = display.length
    ? (page - 1) * (effectivePageSize || display.length) + 1
    : 0;
  const end = display.length ? start + display.length - 1 : 0;

  useEffect(() => {
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount, setPage]);

  const schoolName = toRemove?.org_name || "this school";
  const fallbackCount =
    Number.isFinite(affectedTeachersTotal) && affectedTeachersTotal > 0
      ? affectedTeachersTotal
      : affectedTeachers.length;
  const countLabel = affectedTeachersLoading
    ? "Loading affected teachers..."
    : `${fallbackCount} teacher${fallbackCount === 1 ? "" : "s"} will be affected.`;
  const doubleConfirmCountLabel = affectedTeachersLoading
    ? "Loading affected teachers..."
    : (
      <>
        Total <strong>{fallbackCount}</strong> teacher(s) will be affected.
      </>
    );
  const hasTeachers =
    affectedTeachersError ||
    (Number.isFinite(affectedTeachersTotal)
      ? affectedTeachersTotal
      : affectedTeachers.length) > 0;

  return (
    <View>
      <ConfirmationModal
        show={confirmOpen}
        title="Confirm School Removal"
        message={
          <RemovalMessage>
            <span>You're about to remove {schoolName}.</span>
            <span>
              This action is irreversible and will remove the school and its
              associated teachers from your implementation reports.
            </span>
            <span>{countLabel}</span>
            <span>
              Review the affected teachers below and choose any additional
              deletions.
            </span>
            <span>Are you sure you want to continue?</span>
          </RemovalMessage>
        }
        onCancel={resetRemovalState}
        onConfirm={handleRemovalContinue}
        confirmLabel="Continue"
        confirmDisabled={affectedTeachersLoading}
      >
        <ModalSection>
          <AffectedList colors={colors}>
            {affectedTeachersLoading ? (
              <ListNotice>Loading teachers...</ListNotice>
            ) : affectedTeachersError ? (
              <ListNotice>{affectedTeachersError}</ListNotice>
            ) : affectedTeachers.length === 0 ? (
              <ListNotice>No teachers found for this school.</ListNotice>
            ) : (
              affectedTeachers.map((teacher) => {
                const displayName = getTeacherDisplayName(teacher);
                const meta = getTeacherMeta(teacher);
                const key =
                  teacher.id ||
                  teacher.user_id ||
                  teacher.customer_user_id ||
                  teacher.email ||
                  displayName;
                return (
                  <AffectedRow key={key}>
                    <AffectedName>{displayName}</AffectedName>
                    {meta ? <AffectedMeta>{meta}</AffectedMeta> : null}
                  </AffectedRow>
                );
              })
            )}
          </AffectedList>
          <OptionsGroup>
            <OptionLabel>
              <input
                type="checkbox"
                checked={deleteDownloads}
                onChange={(e) => setDeleteDownloads(e.target.checked)}
                disabled={affectedTeachersLoading}
              />
              <OptionHint>
                Also delete curriculum downloads for teachers who have already
                downloaded them.
              </OptionHint>
            </OptionLabel>
            {/*
            <OptionLabel>
              <input
                type="checkbox"
                checked={deleteCertifications}
                onChange={(e) => setDeleteCertifications(e.target.checked)}
                disabled={affectedTeachersLoading}
              />
              <OptionHint>
                Also remove teacher certifications associated with this school.
              </OptionHint>
            </OptionLabel>
            */}
          </OptionsGroup>
        </ModalSection>
      </ConfirmationModal>
      <ConfirmationModal
        show={doubleConfirmOpen}
        title="Confirm School Removal"
        message={
          <RemovalMessage>
            <span>This is your final confirmation to remove {schoolName}.</span>
            <span>{doubleConfirmCountLabel}</span>
            {hasTeachers ? (
              <>
                <span>
                  Teacher implementations and certifications will be removed.
                </span>
                <span>
                  {deleteDownloads
                    ? "Teacher download history will be deleted."
                    : "Teacher download history will be kept."}
                </span>
              </>
            ) : (
              <span>No teachers will be removed.</span>
            )}
            <span>Are you sure you want to continue?</span>
          </RemovalMessage>
        }
        onCancel={handleDoubleConfirmCancel}
        onConfirm={handleRemoveConfirm}
        confirmLabel="Yes, remove"
        cancelLabel="Back"
      />
      <ConfirmationModal
        show={resultOpen}
        title="Removal Result"
        message={removeMessage}
        onCancel={handleResultClose}
        onConfirm={handleResultClose}
        status={resultStatus}
        single
      />

      {statusMessage && (
        <StatusNotice
          colors={colors}
          statusType={subscriptionStatus}
        >
          {statusMessage}
        </StatusNotice>
      )}

      <Controls colors={colors}>
        <div>
          <label>Show</label>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setPage(1);
            }}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
            <option value={-1}>All</option>
          </select>
          <label style={{ marginLeft: 8 }}>entries</label>
        </div>
        <div>
          <label>Search:</label>
          <input
            type="text"
            value={search}
            style={{ backgroundColor: "#fff" }}
            placeholder="Filter…"
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </Controls>

      <TableWrapper>
        <Table colors={colors}>
          <thead>
            <tr>
              {[
                { key: "org_name", label: "Organization" },
                { key: "org_type_desc", label: "Type" },
                { key: "city", label: "City" },
              ].map((col) => {
                const isAscActive = sortKey === col.key && sortOrder === "ASC";
                const isDescActive = sortKey === col.key && sortOrder === "DESC";
                const defaultColor = "#9CA3AF";
                const activeColor = "#A855F7";
                return (
                  <th key={col.key}>
                    {col.label}
                    <div className="sort-icons">
                      <span
                        onClick={() => handleSort(col.key)}
                        className={isAscActive ? "active" : ""}
                        style={{
                          color: isAscActive ? activeColor : defaultColor,
                          display: isDescActive ? "none" : "block"
                        }}
                      >
                        ▲
                      </span>
                      <span
                        onClick={() => handleSort(col.key)}
                        className={isDescActive ? "active" : ""}
                        style={{
                          color: isDescActive ? activeColor : defaultColor,
                          display: isAscActive ? "none" : "block"
                        }}
                      >
                        ▼
                      </span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {display?.map((sch) => (
              <tr key={sch.school_id}>
                <td>
                  <b>{sch.org_name}</b>
                  <br />
                  NAC School ID: {sch.nac_school_id}
                  <span className="LinkButton">
                    <a
                      style={{fontSize:'12px', color: 'orange' ,fontWeight: 600}}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                         onChange(sch);
                      }}
                    >
                      Change
                    </a>
                  </span>
                  <span
                    className="LinkButton"
                    onClick={() => onRemoveClick(sch)}
                    style={{fontSize:'12px', color: 'orange' ,fontWeight: 600}}
                  >
                    Remove
                  </span>
                </td>
                <td>{sch.org_type_desc}</td>
                <td>{sch.city}</td>
              </tr>
            ))}
            {display?.length === 0 && (
              <tr>
                <td colSpan={3} style={{ textAlign: "center", padding: "1em" }}>
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </TableWrapper>

      <InfoText colors={colors}>
        Showing {display.length ? start : 0} to {display.length ? end : 0} of {total} entries
        {search ? (
          <span>{` (filtered ${display.length})`}</span>
        ) : null}
      </InfoText>

      <Pagination colors={colors}>
        <button
          onClick={() => setPage((p) => Math.max(p - 1, 1))}
          disabled={page === 1}
        >
          Previous
        </button>
        <button
          onClick={() => setPage((p) => Math.min(p + 1, pageCount))}
          disabled={page === pageCount}
        >
          Next
        </button>
      </Pagination>
    </View>
  );
}
